import Enumerable from "linq";
import {
  ISize,
  IDraggingOffsets,
} from "../../../base/domain/drawing";
import { IClonable } from "../../../base/domain/system";
import { AbstractPageStructureEditor } from "../../../base/domain/system/document";
import { IPropertyEditable, IPropertyPresentable } from "../../../base/domain/system/edit";
import { NewObjectKeys } from "../configuration";
import { IFormatSpecification } from "./IFormatSpecification";
import {
  IEditableFormatStructure,
  IEditableFormatPageStructure,
  IEditableFormatObject, instanceOfIEditableFormatObject,
  IEditableFormatImage, IEditableFormatLayer,
} from "./IEditableFormatStructure";
import { IFormatObjectEditable } from "./IFormatObjectEditable";
import { FormatObjectEditorFactory } from "./FormatObjectEditorFactory";
import { DomainRegistry } from "../DomainRegistry";
import { ITextObject } from "./objects";
import { ComparisonUtility } from "./ComparisonUtility";

export class FormatPageStructureEditor extends AbstractPageStructureEditor<IEditableFormatStructure, IEditableFormatPageStructure, IEditableFormatObject, IFormatObjectEditable> implements IClonable<FormatPageStructureEditor> {
  constructor(source: IEditableFormatPageStructure | FormatPageStructureEditor, specification?: IFormatSpecification, isNew: boolean = false) {
    super(source, isNew);
    this._specification =  (source instanceof FormatPageStructureEditor)
      ? source._specification
      : specification;
    this._factory =  (source instanceof FormatPageStructureEditor)
      ? source._factory
      : new FormatObjectEditorFactory(this._specification);
    this._offsets = (source instanceof FormatPageStructureEditor)
      ? source._offsets
      : FormatPageStructureEditor._defaultOffsets;
    this._pageKey = this.createPropertyPresenter(s => s.pageKey);
    this._size = this.createPropertyEditor(s => s.size, (s, v) => {
      s.size = v;
      s.image.size = { width: v.width - (s.image.location.x * 2), height: v.height - (s.image.location.y * 2) };
    });
    this._isHorizontal = this.createPropertyEditor(s => s.isHorizontal, (s, v) => s.isHorizontal = v);
    this._image = this.createPropertyEditor(s => s.image, (s, v) => {
      s.image = v;
      s.size = { width: v.size.width + (s.image.location.x * 2), height: v.size.height + (s.image.location.y * 2) };
    });
    this._layer = this.createPropertyEditor(s => s.layer, (s, v) => s.layer = v);
    this._shouldPrint = this.createPropertyEditor(s => s.shouldPrint, (s, v) => s.shouldPrint = v);
    this._shouldPrintOnFirstRegistered = this.createPropertyEditor(s => s.shouldPrintOnFirstRegistered, (s, v) => s.shouldPrintOnFirstRegistered = v);
    this._editingOnly = this.createPropertyEditor(s => s.editingOnly, (s, v) => s.editingOnly = v);
  }

  private readonly _specification: IFormatSpecification;
  private readonly _factory: FormatObjectEditorFactory;

  updateObject(object: IFormatObjectEditable): void {
    if (!object.isAlreadyLayouted()) {
      console.error("配置されてないオブジェクトを更新しようとしています.実装を確認してください.");
      console.trace();
      return;
    }
    super.updateObject(object);
  }

  addObject(object: IFormatObjectEditable, index?: number): IFormatObjectEditable {
    if (object.isAlreadyLayouted()) {
      console.error("配置済のオブジェクトを追加しようとしています.実装を確認してください.");
      console.trace();
      return null;
    }
    return super.addObject(object, index);
  }

  removeObject(object: IFormatObjectEditable): void {
    if (!object.isAlreadyLayouted()) {
      console.error("配置されてないオブジェクトを削除しようとしています.実装を確認してください.");
      console.trace();
      return;
    }
    super.removeObject(object);
  }

  reconstruct(): FormatPageStructureEditor {
    const reconstructed = this.reconstructObjects();
    if (!reconstructed) {
      return this;
    }
    const cloned = this.clone();
    cloned.reassignObjects(reconstructed);
    return cloned;
  }

  getSpecification(): IFormatSpecification {
    return this._specification;
  }

  private _offsets: IDraggingOffsets<IFormatObjectEditable>;

  getOffsets(): IDraggingOffsets<IFormatObjectEditable> {
    return this._offsets;
  }

  setOffsets(value?: IDraggingOffsets<IFormatObjectEditable>) {
    this._offsets = value
      ? value
      : FormatPageStructureEditor._defaultOffsets;
  }

  private static readonly _defaultOffsets: IDraggingOffsets<IFormatObjectEditable> = {
    source: null,
    relocatingOffset: {
      x: 0,
      y: 0,
    },
    transformingRatesFromCenter: {
      x: 1,
      y: 1,
      width: 1,
      height: 1,
    },
    transformingVector: {
      from: {
        x: 0,
        y: 0,
      },
      to: {
        x: 0,
        y: 0,
      },
    }
  };

  private readonly _pageKey: IPropertyPresentable<number>;

  pageKey(): IPropertyPresentable<number> {
    return this._pageKey;
  }

  private readonly _size: IPropertyEditable<ISize>;

  size(): IPropertyEditable<ISize> {
    return this._size;
  }

  private readonly _isHorizontal: IPropertyEditable<boolean>;

  isHorizontal(): IPropertyEditable<boolean> {
    return this._isHorizontal;
  }

  private readonly _image: IPropertyEditable<IEditableFormatImage>;

  image(): IPropertyEditable<IEditableFormatImage> {
    return this._image;
  }

  private readonly _layer: IPropertyEditable<IEditableFormatLayer>;

  layer(): IPropertyEditable<IEditableFormatLayer> {
    return this._layer;
  }

  private readonly _shouldPrint: IPropertyEditable<boolean>;

  shouldPrint(): IPropertyEditable<boolean> {
    return this._shouldPrint;
  }

  private readonly _shouldPrintOnFirstRegistered: IPropertyEditable<boolean>;

  shouldPrintOnFirstRegistered(): IPropertyEditable<boolean> {
    return this._shouldPrintOnFirstRegistered;
  }

  private readonly _editingOnly: IPropertyEditable<boolean>;

  editingOnly(): IPropertyEditable<boolean> {
    return this._editingOnly;
  }

  protected createObject(source: IEditableFormatObject): IFormatObjectEditable {
    const object = this._factory.create(source);
    Object.freeze(object);
    return object;
  }

  private areSequencesEqual<T>(a: T[], b: T[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }

  protected reconstructObjects(): IFormatObjectEditable[] {
    const source = this.get(s => s.objects, []);
    const editors = this.getObjects();
    if (this.areSequencesEqual(source, Enumerable.from(editors).select(e => e.getSource()).toArray())) {
      return null;  // 追加/削除.
    }
    return Enumerable.from(source)
      .select(o => {
        const found = Enumerable.from(editors)
          .firstOrDefault(e => e.getSource() === o);
        return found
          ? found
          : this.createObject(o);
      })
      .toArray();
  }

  protected toEntities(source: IEditableFormatStructure): IEditableFormatPageStructure[] {
    return source.pages;
  }

  protected toEntity(source: IEditableFormatStructure, entity: IEditableFormatPageStructure): IEditableFormatPageStructure {
    // ソースのインスタンスは置き換えない前提.インスタンス一致で判定.置き換えるような設計変更があるならユニークなキーを用意する事.
    return Enumerable.from(source.pages).firstOrDefault(p => p === entity);
  }

  public createNewObject(source: NewObjectKeys | IEditableFormatObject): IFormatObjectEditable {
    return this._factory.create(
      instanceOfIEditableFormatObject(source)
        ? source
        : this.keyToNewObject(source),
      true);
  }

  private keyToNewObject(key: NewObjectKeys): IEditableFormatObject {
    const result = DomainRegistry.instance.editableFormat.getFormatRepository().createNewFormatObject(key, null);
    // 静的に定義しきれない情報はここで補完する.(オブジェクト固有の実装はあまり入れたくないが...)
    if (key === "NewUserDefinedText" || key === "NewUserDefinedDate") {
      // ユーザ定義のファイル名とキーの初期値は有効なもので補完する.
      const spec = this._specification.userDefinitions;
      if ((0 < spec.length) || (0 < spec[0].columns.length)) {
        const casted = result as ITextObject;
        casted.informationKey = spec[0].columns[0];
        casted.userDefinedInformationSource.fileName = spec[0].name;
      } else {
        console.error("有効なユーザ定義が仕様に含まれていません.");
      }
    }
    return result;
  }

  sortByLocation(): void {
    this.sortObjects((a, b) => ComparisonUtility.compareUpperLeftToRightBottom(a, b));
  }

  clone(): FormatPageStructureEditor {
    return new FormatPageStructureEditor(this, this._specification);
  }
}
